import React, { useState } from 'react';
import {
  CloseCircleOutlined,
  EnvironmentOutlined,
  FileTextOutlined,
  MailOutlined,
  PhoneOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { Button, Carousel, Collapse, Spin, Tag } from 'antd';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';

import { useCreateNote, useGetNotes, useGetQuoteImage, useGetQuoteLineItems, useGetSubQuoteProducts } from '@/features/quotes-management/hooks';
import { QuoteBreakdownSection } from '@/features/quotes-management/components/quotes/QuoteBreakdownSection';
import { QuoteInternalNotes } from '@/features/quotes-management/components/quotes/QuoteInternalNotes';
import { SignatureSection } from '@/features/quotes-management/components/quotes/SignatureSection';
import { WindowConfigurationDisplay } from '@/features/quotes-management/components/quotes/WindowConfigurationDisplay';
import { getEgressBadgeFromSubProducts } from '@/features/quotes-management/components/quotes/detail-shared/helpers';
import { FireEgressBadge } from '@/features/assistant/components/FireEgressBadge';
import { generateOrderAcknowledgementPDF, IPDFHeaderDates } from '@/utils/orderAcknowledgementPDF';
import { getQuoteLineItemsApi, getQuoteDetailFromDataverse } from '@/features/quotes-management/api/dataverse';
import { getCustomerDetailByIdApi } from '@/features/customers/api';
import { formatDealerAmount } from '@/utils/currency';
import { IDataverseQuote } from '@/features/quotes-management/types';

import { useOrderDetail } from '../hooks';
import { ORDER_STATUS_LABELS, OrderStatus, PAYMENT_STATUS_COLORS, PAYMENT_STATUS_LABELS } from '../constants';
import { getOrderNumber, getOrderStatusColor } from '../utils';
import { ROUTESPATH } from '@/routes';

const EgressBadgeLoader: React.FC<{ quotedetailid: string }> = ({ quotedetailid }) => {
  const { data } = useGetSubQuoteProducts(quotedetailid);
  const subs = data?.value ?? [];
  const status = getEgressBadgeFromSubProducts(subs);
  if (!status) return null;
  return <FireEgressBadge status={status} />;
};

interface OrderDetailViewProps {
  orderId: string;
  onChangeOrderStatus?: () => void;
  onResendPaymentLink?: () => void;
  onEditEvolutionJobNumber?: () => void;
  /** When rendered inside the table as an expanded row */
  isInline?: boolean;
}

export const OrderDetailView: React.FC<OrderDetailViewProps> = ({
  orderId,
  onChangeOrderStatus,
  onResendPaymentLink,
  onEditEvolutionJobNumber,
  isInline = false,
}) => {
  const { data: order, isLoading, error } = useOrderDetail(orderId);
  const quoteId = order?.quoteid?.quoteid ?? '';
  const { data: quoteImageResponse, isLoading: isImageLoading } = useGetQuoteImage(quoteId);
  const { data: quoteLineItems, isLoading: isLineItemsLoading } = useGetQuoteLineItems(quoteId);
  const { data: notesResponse, isLoading: isNotesLoading } = useGetNotes('quote', quoteId);
  const createNoteMutation = useCreateNote();

  const [isDownloadingPDF, setIsDownloadingPDF] = useState(false);
  const navigate = useNavigate();

  const handleAddNote = (note: string) => {
    if (!quoteId) return;
    createNoteMutation.mutate({
      entityType: 'quote',
      recordId: quoteId,
      subject: 'Customer Note',
      noteText: note,
    });
  };

  const lineItems = quoteLineItems?.value || [];

  const signatureImage = quoteImageResponse?.images?.find((img: any) =>
    img.fileName?.toLowerCase().includes('signature')
  );
  const propertyImages =
    quoteImageResponse?.images?.filter(
      (img: any) => !img.fileName?.toLowerCase().includes('signature')
    ) || [];

  const handleDownloadPDF = async () => {
    if (!order || lineItems.length === 0) {
      toast.error('Order data is not available');
      return;
    }

    setIsDownloadingPDF(true);
    try {
      const accountId = order.quoteid?._dogma_customer_value;
      const [lineItemsResponse, quoteDetail, accountDetail] = await Promise.all([
        getQuoteLineItemsApi(quoteId),
        getQuoteDetailFromDataverse(quoteId),
        accountId ? getCustomerDetailByIdApi(accountId) : Promise.resolve(null),
      ]);
      const items = lineItemsResponse?.value || [];

      const quoteData: IDataverseQuote = {
        ...quoteDetail,
        quotenumber: getOrderNumber(order),
        createdon: order.createdon,
        dogma_vsssubtotal: order.dogma_vsssubtotal,
        dogma_vssvatamount: order.dogma_vssvatamount,
        dogma_vsstotalamount: order.dogma_vsstotalamount,
        dogma_vssdiscountamount: order.dogma_vssdiscountamount || 0,
        dogma_customer: accountDetail ? {
          accountid: accountDetail.accountid,
          name: accountDetail.name,
          address1_composite: accountDetail.address1_composite,
          telephone1: accountDetail.telephone1,
          primarycontactid: accountDetail.primarycontactid,
        } : quoteDetail.dogma_customer,
      };

      const headerDates: IPDFHeaderDates = {
        orderDate: order.quoteid?.createdon
          ? dayjs(order.quoteid.createdon).format('DD-MMM-YYYY')
          : '',
        processedDate: order.createdon ? dayjs(order.createdon).format('DD-MMM-YYYY') : '',
        productionDate: order.createdon
          ? dayjs(order.createdon).add(15, 'day').format('DD-MMM-YYYY')
          : '',
        printedDate: dayjs().format('DD-MMM-YYYY'),
        deliveryDate: order.requestdeliveryby ? dayjs(order.requestdeliveryby).format('DD-MMM-YYYY') : '',
        evolutionJobNumber: order.dogma_evolutionjobnumber ?? undefined,
      };

      await generateOrderAcknowledgementPDF(quoteData, items, headerDates, 'Order Acknowledgement');
    } catch (e) {
      toast.error('Failed to generate PDF');
    } finally {
      setIsDownloadingPDF(false);
    }
  };

  if (isLoading || isLineItemsLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spin size="large" />
      </div>
    );
  }

  if (error || !order) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <CloseCircleOutlined className="text-red-500 text-4xl mb-2" />
          <p className="text-red-600">
            {error ? 'Failed to load order details' : 'Order not found'}
          </p>
        </div>
      </div>
    );
  }

  const orderDate = order.createdon ? dayjs(order.createdon).format('DD-MM-YYYY') : 'N/A';

  const dealersCurrency = order.quoteid?.dogma_DealersCurrency;
  const exchangeRate = order.quoteid?.dogma_exchangerate ?? dealersCurrency?.dogma_exchangerate ?? 1;
  const formatAmount = (amount: number) =>
    formatDealerAmount(amount, dealersCurrency, exchangeRate);

  const customerDetails = {
    name: order['_customerid_value@OData.Community.Display.V1.FormattedValue'] || 'N/A',
    email: order.customerid_contact?.emailaddress1 ?? 'N/A',
    phone: order.customerid_contact?.telephone1 ?? 'N/A',
    address: order.customerid_contact?.address1_line1 || order.billto_line1 || 'N/A',
    postCode:
      order.customerid_contact?.address1_postalcode || order.billto_postalcode || 'N/A',
    projectRef: order.quoteid?.dogma_projectreference || 'N/A',
    quoteNumber: order.quoteid?.quotenumber || 'N/A',
  };

  const orderStatusLabel =
    order['statuscode@OData.Community.Display.V1.FormattedValue'] ||
    ORDER_STATUS_LABELS[order.statuscode] ||
    'Unknown';
  const orderStatusColor = getOrderStatusColor(order.statuscode, orderStatusLabel);

  const paymentStatusLabel =
    order['dogma_paymentstatus@OData.Community.Display.V1.FormattedValue'] ||
    PAYMENT_STATUS_LABELS[order.dogma_paymentstatus] ||
    'Unknown';
  const paymentStatusColor = PAYMENT_STATUS_COLORS[order.dogma_paymentstatus] || 'default';

  return (
    <div className="w-full bg-gray-50">
      {/* Header — hidden when rendered inline inside the table */}
      {!isInline && (
        <div className="bg-white rounded-lg p-4 md:p-6 mb-6">
          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">
                Order {getOrderNumber(order)}
              </h1>
              <div className="flex flex-wrap items-center gap-2 mb-2">
                <Tag
                  color={orderStatusColor}
                  style={{ borderRadius: '12px', padding: '4px 12px', border: 'none' }}
                >
                  {orderStatusLabel}
                </Tag>
                <Tag
                  color={paymentStatusColor}
                  style={{ borderRadius: '12px', padding: '4px 12px', border: 'none' }}
                >
                  {paymentStatusLabel}
                </Tag>
              </div>
              <div className="flex flex-wrap items-center gap-3 text-sm text-gray-600">
                <span>Created: {orderDate}</span>
                {order.datefulfilled && (
                  <span>
                    Fulfilled: {dayjs(order.datefulfilled).format('DD-MM-YYYY')}
                  </span>
                )}
              </div>
            </div>
            {order.paymenttermscode === 1 && (
              <Button
                type="primary"
                onClick={onResendPaymentLink}
              >
                Resend Payment Link
              </Button>
            )}
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left Column */}
        <div className="space-y-6">
          {/* Customer Details */}
          <div className="bg-white rounded-lg p-6">
            <h3 className="text-lg text-left font-semibold text-gray-900 mb-4">
              Customer Details
            </h3>
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <UserOutlined className="text-gray-400 text-lg flex-shrink-0" />
                <span className="text-sm text-gray-600 min-w-[100px]">Name</span>
                <p className="text-sm text-gray-900 font-medium">{customerDetails.name}</p>
              </div>
              <div className="flex items-center gap-3">
                <MailOutlined className="text-gray-400 text-lg flex-shrink-0" />
                <span className="text-sm text-gray-600 min-w-[100px]">Email</span>
                <p className="text-sm text-gray-900 font-medium">{customerDetails.email}</p>
              </div>
              <div className="flex items-center gap-3">
                <PhoneOutlined className="text-gray-400 text-lg flex-shrink-0" />
                <span className="text-sm text-gray-600 min-w-[100px]">Phone</span>
                <p className="text-sm text-gray-900 font-medium">{customerDetails.phone}</p>
              </div>
              <div className="flex items-center gap-3">
                <EnvironmentOutlined className="text-gray-400 text-lg flex-shrink-0" />
                <span className="text-sm text-gray-600 min-w-[100px]">Address</span>
                <p className="text-sm text-gray-900 font-medium">{customerDetails.address}</p>
              </div>
              <div className="flex items-center gap-3">
                <EnvironmentOutlined className="text-gray-400 text-lg flex-shrink-0" />
                <span className="text-sm text-gray-600 min-w-[100px]">Post Code</span>
                <p className="text-sm text-gray-900 font-medium">{customerDetails.postCode}</p>
              </div>
              <div className="flex items-center gap-3">
                <FileTextOutlined className="text-gray-400 text-lg flex-shrink-0" />
                <span className="text-sm text-gray-600 min-w-[100px]">Project Ref.</span>
                <p className="text-sm text-gray-900 font-medium">{customerDetails.projectRef}</p>
              </div>
              <div className="flex items-center gap-3">
                <FileTextOutlined className="text-gray-400 text-lg flex-shrink-0" />
                <span className="text-sm text-gray-600 min-w-[100px]">Quote No.</span>
                {quoteId && order.quoteid?.quotenumber ? (
                  <p
                    className="text-sm text-primary-500 font-medium cursor-pointer hover:underline"
                    onClick={() => navigate(ROUTESPATH.QUOTE_DETAIL.replace(':id', quoteId))}
                  >
                    {customerDetails.quoteNumber}
                  </p>
                ) : (
                  <p className="text-sm text-gray-900 font-medium">{customerDetails.quoteNumber}</p>
                )}
              </div>
            </div>
          </div>

          {/* Property Images */}
          {!isImageLoading && quoteImageResponse?.success && propertyImages.length > 0 && (
            <div className="bg-white rounded-lg p-6">
              <h3 className="text-lg text-left font-semibold text-gray-900 mb-4">
                Property Images ({propertyImages.length})
              </h3>
              {propertyImages.length === 1 ? (
                <div className="flex justify-center">
                  <img
                    src={propertyImages[0].imageUrl}
                    alt="Property"
                    className="max-w-full h-auto rounded-lg border border-gray-200"
                    style={{ maxHeight: '350px' }}
                  />
                </div>
              ) : (
                <div className="relative">
                  <Carousel
                    autoplay
                    autoplaySpeed={3000}
                    arrows
                    className="property-images-carousel [&_.slick-arrow]:text-primary-500"
                  >
                    {propertyImages.map((image: any, index: number) => (
                      <div key={index}>
                        <div
                          className="flex justify-center items-center"
                          style={{ minHeight: '300px' }}
                        >
                          <img
                            src={image.imageUrl}
                            alt={`Property image ${index + 1}`}
                            className="max-w-full h-auto rounded-lg border border-gray-200"
                            style={{ maxHeight: '350px', margin: '0 auto' }}
                          />
                        </div>
                      </div>
                    ))}
                  </Carousel>
                </div>
              )}
            </div>
          )}

          {/* Signature */}
          <SignatureSection
            signature={signatureImage?.imageUrl || ''}
            signedDate={orderDate}
          />

          {/* Order Breakdown */}
          {lineItems.length > 0 && (
            <QuoteBreakdownSection
              lineItems={lineItems}
              subtotal={order.dogma_vsssubtotal ?? 0}
              discountAmount={order.dogma_vssdiscountamount ?? 0}
              discountPercentage={order.quoteid?.dogma_vssdiscountpercentage}
              dealersCurrency={dealersCurrency}
              exchangeRate={exchangeRate}
            />
          )}

          {/* Order Summary */}
          <div className="bg-white rounded-lg p-6">
            <h3 className="text-lg text-left font-semibold text-gray-900 mb-4">
              Order Summary
            </h3>
            <div className="space-y-3">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Subtotal (Gross)</span>
                <span className="font-semibold text-gray-900">
                  {formatAmount(order.dogma_vsssubtotal ?? 0)}
                </span>
              </div>
              {(order.dogma_vssdiscountamount ?? 0) > 0 && (
                <>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">
                      Discount Amount{order.quoteid?.dogma_vssdiscountpercentage ? ` (${order.quoteid.dogma_vssdiscountpercentage}%)` : ''}
                    </span>
                    <span className="text-red-500 font-semibold">
                      -{formatAmount(order.dogma_vssdiscountamount ?? 0)}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm font-bold">
                    <span className="text-gray-900">After Discount</span>
                    <span className="text-gray-900">
                      {formatAmount((order.dogma_vsssubtotal ?? 0) - (order.dogma_vssdiscountamount ?? 0))}
                    </span>
                  </div>
                </>
              )}
              {(order.freightamount ?? 0) > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Delivery Charge</span>
                  <span className="font-semibold text-gray-900">{formatAmount(order.freightamount ?? 0)}</span>
                </div>
              )}
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">VAT ({dealersCurrency?.dogma_vatpercentage ?? 20}%)</span>
                <span className="font-semibold text-gray-900">{formatAmount(order.dogma_vssvatamount ?? 0)}</span>
              </div>
              <div className="h-px bg-gray-200" />
              <div className="flex justify-between text-lg font-bold">
                <span className="text-gray-900">Total Amount</span>
                <span className="text-primary">{formatAmount(order.dogma_vsstotalamount ?? 0)}</span>
              </div>
            </div>
          </div>

          {/* Window Configuration */}
          {lineItems.length > 0 && (
            <div className="bg-white rounded-lg p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Window Configuration
              </h3>
              <Collapse
                className="mb-4"
                defaultActiveKey={lineItems.map((item) => item.quotedetailid)}
              >
                {lineItems.map((lineItem, index) => (
                  <Collapse.Panel
                    key={lineItem.quotedetailid}
                    header={
                      <span className="font-semibold text-gray-900">
                        {lineItem.dogma_windowname ||
                          lineItem.productname ||
                          `Frame ${index + 1}`}
                      </span>
                    }
                  >
                    <EgressBadgeLoader quotedetailid={lineItem.quotedetailid} />
                    <WindowConfigurationDisplay
                      quoteProductId={lineItem.quotedetailid}
                      subtotal={lineItem.dogma_vsssubtotal ?? 0}
                      dealersCurrency={dealersCurrency}
                      exchangeRate={exchangeRate}
                    />
                  </Collapse.Panel>
                ))}
              </Collapse>
            </div>
          )}
        </div>

        {/* Right Column */}
        <div className="space-y-6">
          {/* Status Management */}
          <div className="bg-white rounded-lg p-6">
            <h3 className="text-lg text-left font-semibold text-gray-900 mb-4">
              Status Management
            </h3>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600 uppercase font-medium">
                  Order Status
                </span>
                <Tag
                  color={orderStatusColor}
                  style={{ borderRadius: '12px', padding: '4px 12px', border: 'none' }}
                >
                  {orderStatusLabel}
                </Tag>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600 uppercase font-medium">
                  Payment Status
                </span>
                <Tag
                  color={paymentStatusColor}
                  style={{ borderRadius: '12px', padding: '4px 12px', border: 'none' }}
                >
                  {paymentStatusLabel}
                </Tag>
              </div>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="bg-white rounded-lg p-6">
            <h3 className="text-xl text-left font-bold text-gray-900 mb-4">Quick Actions</h3>
            <div className="grid grid-cols-2 gap-3">
              {isInline && (
                <Button
                  type="primary"
                  className="h-12 text-sm font-medium"
                  onClick={() => navigate(ROUTESPATH.ORDER_DETAIL.replace(':id', orderId))}
                  block
                >
                  View Full Order
                </Button>
              )}
              <Button
                className="h-12 text-sm font-medium"
                onClick={handleDownloadPDF}
                loading={isDownloadingPDF}
                disabled={isLoading || isLineItemsLoading || !quoteId}
                block
              >
                {isDownloadingPDF ? 'Generating...' : 'Download PDF'}
              </Button>
              <Button
                className="h-12 text-sm font-medium bg-blue-500 hover:bg-blue-600 text-white border-blue-500"
                onClick={onChangeOrderStatus}
                block
              >
                Change Order Status
              </Button>

              {(order?.statuscode === OrderStatus.IN_PRODUCTION || order?.statuscode === OrderStatus.Completed) && (
                <Button
                  className="h-12 text-sm font-medium"
                  onClick={onEditEvolutionJobNumber}
                  block
                >
                  {order?.dogma_evolutionjobnumber ? `Evolution Job No: ${order.dogma_evolutionjobnumber}` : 'Edit Evolution Job Number'}
                </Button>
              )}

              {!isInline && (
                <Button
                  className="h-12 text-sm font-medium"
                  onClick={() => navigate('/orders')}
                  block
                >
                  Back to Orders
                </Button>
              )}
            </div>
          </div>

          {/* Internal Notes — added here but stored against the quote record */}
          <QuoteInternalNotes
            notes={notesResponse?.value}
            isLoadingNotes={isNotesLoading}
            onAddNote={handleAddNote}
          />
        </div>
      </div>
    </div>
  );
};
